import React, { useState, useMemo, useCallback } from 'react';
import { Table, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import AIAnalysisModal from '../../pages/KeywordAnalysis/AIAnalysisModal';
import { analyzeSingleKeyword, parseApiError } from '../../services/aiAnalysisService';
import { createAIActionColumns } from '../../config/tableColumns/keyword';
import type { SingleAnalysisResult } from '../../types/ai-analysis-types';
import { useAIConfig } from '../../contexts/AIConfigContext';
import './KeywordsTab.css';

interface KeywordsTabProps {
  keywords: any[];
  columns: ColumnsType<any>;
}

/**
 * 关键词指标Tab组件
 * 展示聚合的关键词数据和跨周期指标
 */
export const KeywordsTab: React.FC<KeywordsTabProps> = ({ keywords, columns }) => {
  const { configId } = useAIConfig();
  const [aiModalVisible, setAiModalVisible] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState<SingleAnalysisResult | null>(null);
  const [currentKeyword, setCurrentKeyword] = useState<string>('');

  // 处理智能分析（使用当前 SKU 的配置）
  const handleAIAnalysis = useCallback(async (record: any) => {
    if (!record.id) {
      message.error('关键词 ID 不存在，无法进行分析');
      return;
    }

    setCurrentKeyword(record.keyword || '未知关键词');
    setAiModalVisible(true);
    setAiLoading(true);
    setAiResult(null);

    try {
      // 传递配置ID（如果存在）和使用聚合数据标志
      const requestData = {
        keyword_id: record.id,
        use_aggregated: true,  // 默认使用聚合数据（跨周期平均值）
        ...(configId && { config_id: configId }),
      };
      const result = await analyzeSingleKeyword(requestData);
      setAiResult(result);
    } catch (error) {
      message.error(`智能分析失败: ${parseApiError(error)}`);
      setAiModalVisible(false);
    } finally {
      setAiLoading(false);
    }
  }, [configId]);

  // 组合列定义：原有列 + AI 操作列
  const finalColumns = useMemo(() => {
    const aiColumns = createAIActionColumns(handleAIAnalysis);
    return [...columns, ...aiColumns];
  }, [columns, handleAIAnalysis]);

  return (
    <div className="keywords-table">
      <Table
        dataSource={keywords}
        columns={finalColumns}
        rowKey="keyword"
        tableLayout="auto"
        scroll={{ x: 'max-content', y: 'calc(100vh - 380px)' }}
        sticky={{ offsetHeader: 0 }}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 个关键词`,
          pageSizeOptions: ['10', '20', '50', '100'],
        }}
        bordered
        size="small"
      />

      <AIAnalysisModal
        visible={aiModalVisible}
        loading={aiLoading}
        analysisResult={aiResult}
        keywordName={currentKeyword}
        onClose={() => setAiModalVisible(false)}
      />
    </div>
  );
};

